// src/hooks/useLocation.ts
import { useState, useEffect, useCallback } from 'react';
import { useGeolocation } from 'react-use';
import { Location, GeolocationError } from '../types';
import { getPrivacySettings, sanitizeLocation, requestLocationPermission } from '../utils/privacy';
import { useUI } from '../contexts/UIContext';
import { maybeWriteLastLocation } from '../services/lastLocation';

export const useLocation = () => {
  const { state: uiState, dispatch } = useUI();
  const [location, setLocation] = useState<Location | null>(null);
  const [error, setError] = useState<GeolocationError | null>(null);
  const [hasPermission, setHasPermission] = useState<boolean>(false);

  // Get user's privacy settings
  const privacySettings = getPrivacySettings();

  const geolocation = useGeolocation({
    enableHighAccuracy: privacySettings.allowPreciseLocation, // Use setting for accuracy
    timeout: 10000,
    maximumAge: privacySettings.allowPreciseLocation ? 60000 : 300000, // Shorter cache for precision mode
  });

  useEffect(() => {
    if (geolocation.loading) {
      dispatch({ type: 'SET_LOCATION_LOADING', payload: true });
    } else {
      dispatch({ type: 'SET_LOCATION_LOADING', payload: false });
    }

    if (geolocation.latitude && geolocation.longitude) {
      const rawLocation: Location = {
        latitude: geolocation.latitude,
        longitude: geolocation.longitude,
        accuracy: geolocation.accuracy || undefined,
        timestamp: geolocation.timestamp || Date.now(),
      };

      // Apply privacy settings to sanitize location
      const sanitizedLocation = sanitizeLocation(rawLocation, privacySettings);
      setLocation(sanitizedLocation);
      setHasPermission(true);
      setError(null);

      // Persist last-known location to the user doc for the
      // curator's 30-min heartbeat (Phase 3, see CURATOR_NOTES.md).
      // Internally throttled to once per 5 minutes, gated to
      // registered users only, gated by accuracy. Fire-and-forget
      // — failures are logged but never surface here.
      void maybeWriteLastLocation({
        latitude: sanitizedLocation.latitude,
        longitude: sanitizedLocation.longitude,
        accuracy: sanitizedLocation.accuracy,
      });
    }

    if (geolocation.error) {
      const geoError: GeolocationError = {
        code: (geolocation.error as any).code || 0,
        message: geolocation.error.message,
      };
      setError(geoError);
      setHasPermission(false);
      
      if ((geolocation.error as any).code === 1) {
        dispatch({ type: 'SET_SHOW_LOCATION_PERMISSION', payload: true });
      }
    }
  }, [geolocation, dispatch, privacySettings]);

  const requestPermission = useCallback(async () => {
    try {
      dispatch({ type: 'SET_LOCATION_LOADING', payload: true });
      
      // Use current privacy settings for permission request
      const currentSettings = getPrivacySettings();
      const position = await requestLocationPermission(currentSettings);
      
      const rawLocation: Location = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
        timestamp: position.timestamp,
      };

      // Apply privacy settings to sanitize location
      const sanitizedLocation = sanitizeLocation(rawLocation, currentSettings);
      setLocation(sanitizedLocation);
      setHasPermission(true);
      setError(null);
      dispatch({ type: 'SET_SHOW_LOCATION_PERMISSION', payload: false });
    } catch (err) {
      const geoError: GeolocationError = {
        code: (err as GeolocationPositionError).code || 0,
        message: (err as GeolocationPositionError).message || 'Location request failed',
      };
      setError(geoError);
      setHasPermission(false);
    } finally {
      dispatch({ type: 'SET_LOCATION_LOADING', payload: false });
    }
  }, [dispatch]);

  const refresh = useCallback(() => {
    if (hasPermission) {
      requestPermission();
    }
  }, [hasPermission, requestPermission]);

  return {
    location,
    loading: uiState.isLocationLoading,
    error,
    hasPermission,
    requestPermission,
    refresh,
  };
};